import React, { useEffect, useState } from 'react';
import {
	useFloating,
	useInteractions,
	useClick,
	useDismiss,
	useRole,
	autoUpdate,
	offset,
	flip,
	shift,
	FloatingFocusManager,
	FloatingPortal
} from '@floating-ui/react';

const BulkActionButton = ({ label, items, isDanger, onClick }) => {
	const [isOpen, setIsOpen] = useState(false);

	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		middleware: [offset(4), flip(), shift()],
		whileElementsMounted: autoUpdate,
		placement: 'bottom-start'
	});

	const click = useClick(context, { preventScroll: true });
	const dismiss = useDismiss(context);
	const role = useRole(context);

	const { getReferenceProps, getFloatingProps } = useInteractions([
		click,
		dismiss,
		role
	]);

	const handleItemClick = item => {
		if (item.onClick) {
			item.onClick();
			setIsOpen(false);
		}
	};

	const buttonClassName = `bulk-action-btn${isDanger ? ' is-danger' : ''}`;

	// If no items, it's a simple button
	if (!items || items.length === 0) {
		return (
			<button className={buttonClassName} onClick={onClick} type="button">
				{label}
			</button>
		);
	}

	return (
		<div className={`bulk-action-dropdown ${isOpen ? 'is-active' : ''}`}>
			<button
				className={buttonClassName}
				ref={refs.setReference}
				{...getReferenceProps()}
				type="button">
				{label}
			</button>

			<FloatingPortal>
				{isOpen && (
					<FloatingFocusManager context={context} modal={false}>
						<ul
							ref={refs.setFloating}
							style={floatingStyles}
							{...getFloatingProps()}
							className="bulk-action-menu">
							{items.map((item, index) => (
								<li
									key={index}
									className="bulk-action-menu-item"
									onClick={() => handleItemClick(item)}>
									{item.icon && <img src={item.icon} alt="" />}
									{item.label}
								</li>
							))}
						</ul>
					</FloatingFocusManager>
				)}
			</FloatingPortal>
		</div>
	);
};

const BULK_BAR_TRANSITION_MS = 280;

const BulkActionsBar = ({ selectedCount, actions, onClearSelection }) => {
	const isActive = selectedCount > 0;
	const [shouldRender, setShouldRender] = useState(isActive);
	const [isVisible, setIsVisible] = useState(false);

	useEffect(() => {
		if (isActive) {
			setShouldRender(true);
			// Double rAF ensures the hidden styles paint before transitioning in.
			let innerFrame;
			const outerFrame = requestAnimationFrame(() => {
				innerFrame = requestAnimationFrame(() => {
					setIsVisible(true);
				});
			});

			return () => {
				cancelAnimationFrame(outerFrame);
				cancelAnimationFrame(innerFrame);
			};
		}

		setIsVisible(false);
	}, [isActive]);

	useEffect(() => {
		if (!isVisible && !isActive) {
			const timeout = setTimeout(
				() => setShouldRender(false),
				BULK_BAR_TRANSITION_MS
			);

			return () => clearTimeout(timeout);
		}
	}, [isActive, isVisible]);

	if (!shouldRender) {
		return null;
	}

	const primaryActions = actions.filter(action => !action.isDanger);
	const dangerActions = actions.filter(action => action.isDanger);

	return (
		<div
			className={`bulk-actions-bar-shell${
				isVisible ? ' bulk-actions-bar-shell--visible' : ''
			}`}
			aria-hidden={!isVisible}>
			<div className="bulk-actions-bar-shell-inner">
				<div className="BulkActionsBar">
					<div className="bulk-actions-content">
						{primaryActions.length > 0 && (
							<div className="bulk-actions-primary">
								{primaryActions.map((action, index) => (
									<BulkActionButton
										key={index}
										label={action.label}
										items={action.items}
										isDanger={action.isDanger}
										onClick={action.onClick}
									/>
								))}
							</div>
						)}
						{dangerActions.length > 0 && (
							<>
								{primaryActions.length > 0 && (
									<div className="bulk-actions-divider" aria-hidden="true" />
								)}
								<div className="bulk-actions-danger">
									{dangerActions.map((action, index) => (
										<BulkActionButton
											key={index}
											label={action.label}
											items={action.items}
											isDanger={action.isDanger}
											onClick={action.onClick}
										/>
									))}
								</div>
							</>
						)}
					</div>
					{onClearSelection && (
						<button
							type="button"
							className="bulk-actions-clear-btn button-bg-soft-small is-icon-only"
							aria-label="Clear selection"
							onClick={onClearSelection}>
							<img src="/ats/icons/close-bulk-icon.svg" alt="" />
						</button>
					)}
				</div>
			</div>
		</div>
	);
};

export default BulkActionsBar;
